import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import ImportAllConfirmModal from './ImportAllConfirmModal'; // import ไฟล์ที่สร้างใหม่
import SignalManagementPopup from './SignalManagementPopup';
import { cryptoPairLabel } from '../composables/fetchCrypto';

// ค้นหาได้ทั้งชื่อจริง (X:BTCUSD) และชื่อที่แสดง (BTC/USD) → พิมพ์ BTC ก็เจอ
const tickerSearchText = (ticker: string) => `${ticker} ${cryptoPairLabel(ticker)}`.toLowerCase();

interface SignalUpdate {
  id: string;
  ticker: string;
  created_at: string;
  lastUpdated: string;
  details: {
    lines: Array<{
      tag: string;
      price: number;
      type: string;
    }>;
    lastUpdated: string;
  };
  onSelectSymbol?: (symbol: string) => void;
  portfolios: any[]; // เพิ่ม props สำหรับพอร์ตโฟลิโอ (ถ้าจำเป็นสำหรับ Popup)
  signalData: any[]; // ข้อมูล 91 ตัว (อัปเดต)
  allHistoryData: any[]; // ข้อมูล 900+ ตัว (ประวัติทั้งหมด)
}

interface SignalUpdateListProps {
  updates: SignalUpdate[];
  loading: boolean;
  error: string | null;
  onRefresh: (isFullSearch: boolean) => void;
  onSelectSymbol?: (symbol: string) => void;
  onImportClick: (ticker: string | string[], isBulk?: boolean) => void;
  portfolios: any[]; // เพิ่ม props สำหรับพอร์ตโฟลิโอ (ถ้าจำเป็นสำหรับ Popup)
  signalData: any[]; // ข้อมูล 91 ตัว (อัปเดต)
  allHistoryData: any[]; // ข้อมูล 900+ ตัว (ประวัติทั้งหมด)
}

const SignalUpdateList: React.FC<SignalUpdateListProps> = ({ updates, loading, error, onRefresh, onSelectSymbol, onImportClick, portfolios, signalData, allHistoryData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAllModalOpen, setIsAllModalOpen] = useState(false);
  // ใน SignalUpdateList.tsx
  const [isManagePopupOpen, setIsManagePopupOpen] = useState(false);

  // 1. ดึงรายชื่อหุ้นที่ซ่อน
  const hiddenList = JSON.parse(localStorage.getItem('ticker_hide') || '[]');

  // 2. กรองข้อมูล updates ก่อนเอาไปแสดงผล (ข้างนอก popup)
  const filteredUpdates = updates.filter(u => !hiddenList.includes(u.ticker));

  const isNewSignal = (dateString: string) => {
    if (!dateString) return false;
    const now = new Date().getTime();
    const updatedTime = new Date(dateString).getTime();
    const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    return (now - updatedTime) < twentyFourHoursInMs;
  };
  // 3. ใช้ filteredUpdates แทน updates ใน displayUpdates/processedUpdates เดิม
  // (เพื่อให้ในช่องค้นหาหรือในลิสต์ไม่โชว์ตัวที่ซ่อนไว้)
  const handleConfirmImportAll = async () => {
    setIsAllModalOpen(false);

    // เตรียม Array ของ Tickers และ Signals ทั้งหมด
    const allTickers = displayUpdates.map(u => u.ticker);
    const allSignals = displayUpdates; // ส่งก้อน updates ไปเลย

    // เรียกใช้ฟังก์ชันเดียว จบ!
    // หมายเหตุ: พี่อั้มต้องปรับ onImportClick ให้รองรับ Array ในไฟล์ HomePage ด้วยนะ
    await onImportClick(allTickers, true);
  };

  // ✅ ฟังก์ชันจัดการเมื่อมีการเลือกหุ้น
  const handleSelect = (ticker: string) => {
    // 1. บันทึกลง Storage
    localStorage.setItem('selectedSymbol', ticker);

    // 2. เรียก Callback เพื่อเปลี่ยนหุ้นในหน้าจอหลัก (ถ้ามีส่งมา)
    if (onSelectSymbol) {
      onSelectSymbol(ticker);
    } else {
      // ถ้าไม่มี Callback อาจจะใช้ reload เพื่อให้หน้าจอรับค่าใหม่จาก Storage
      window.location.reload();
    }
  };

  // ฟังก์ชันสำหรับจัดการข้อมูลแนวรับ (น้อย -> มาก)
  const renderSupports = (lines: any[]) => {
    return lines
      .filter(l => l.type === 'support')
      .sort((a, b) => a.price - b.price) // น้อยไปมาก
      .map(l => `$${l.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}`)
      .join(', ');
  };
  // ฟังก์ชันสำหรับจัดการข้อมูลแนวต้าน (มาก -> น้อย)
  const renderResistances = (lines: any[]) => {
    return lines
      .filter(l => l.type === 'resistance')
      .sort((a, b) => b.price - a.price) // มากไปน้อย
      .map(l => `$${l.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}`)
      .join(', ');
  };

  // กรองข้อมูล: หาตัวที่ตรงกับคำค้นหา และเอาขึ้นก่อน
  const processedUpdates = useMemo(() => {
    if (!updates) return [];

    // กรองตาม Ticker
    const filtered = updates.filter(u =>
      tickerSearchText(u.ticker).includes(searchTerm.toLowerCase())
    );

    // ถ้าไม่ได้ค้นหา ให้คืนค่าเดิม
    if (!searchTerm) return updates;

    // ถ้าค้นหา: เรียงเอาตัวที่ "ขึ้นต้นด้วย" คำที่พิมพ์มาไว้หน้าสุด
    return [...filtered].sort((a, b) => {
      const aStarts = cryptoPairLabel(a.ticker).toLowerCase().startsWith(searchTerm.toLowerCase());
      const bStarts = cryptoPairLabel(b.ticker).toLowerCase().startsWith(searchTerm.toLowerCase());
      return aStarts === bStarts ? 0 : aStarts ? -1 : 1;
    });
  }, [updates, searchTerm]);
  const displayUpdates = processedUpdates.filter(u => !hiddenList.includes(u.ticker));


  useEffect(() => {
    // ✅ เมื่อไหร่ก็ตามที่ popup ถูกเปิด ให้แอบ fetch ข้อมูลทั้งหมดรอไว้เลย
    // เพื่อให้ tabCounts ใน popup มีข้อมูลไปนับเลขได้ทันที
    if (isManagePopupOpen) {
      onRefresh(true);
    }
  }, [isManagePopupOpen, onRefresh]);

  return (
    <div className="w-full bg-[var(--component-bg-color)] rounded-xl p-4 shadow-lg ">
      <div className="flex justify-between border-b border-[var(--border-item-dark)] pb-2 items-center mb-2">
        <h3 className="text-base font-bold text-[var(--text-color)] flex items-center gap-2">
          🔔 อัปเดตแนวรับ-ต้าน
        </h3>
        <button
          onClick={() => onRefresh(false)}
          disabled={loading}
          className="text-lg text-[var(--text-color)] px-3 py-1 rounded-md transition-all active:scale-95 disabled:opacity-50"
        >
          {loading ? '...' : '🔄'}
        </button>
      </div>

      <div className='flex justify-between gap-2'>

        <button
          onClick={() => setIsAllModalOpen(true)} // เปลี่ยนจาก window.confirm เป็นเปิด Modal
          disabled={loading || displayUpdates.length === 0}
          className="text-xs bg-[var(--button-bg-color)] hover:brightness-110 mb-2 text-[var(--text-color)] px-3 py-1 rounded-md transition-all active:scale-95 disabled:opacity-50 font-bold"
        >
          Import All ({displayUpdates.length})
        </button>
        <div className="text-xs mb-2 bg-yellow-300 text-yellow-800 gap-1 cursor-pointer hover:text-black hover:bg-yellow-400 px-2 py-1 rounded-md transition-all active:scale-95 flex items-center"
          onClick={() => {
            onRefresh(false); // ✅ สั่ง Fetch ข้อมูลใหม่ทันที
            setIsManagePopupOpen(true);
          }}
        >
          ดูทั้งหมด
        </div>
        <SignalManagementPopup
          isOpen={isManagePopupOpen}
          onClose={() => setIsManagePopupOpen(false)}
          updates={signalData}          // ข้อมูล 91 ตัว (อัปเดต)
          allHistoryData={allHistoryData}   // ข้อมูล 900+ ตัว (ประวัติทั้งหมด)
          onImportSelected={(tickers) => onImportClick(tickers, true)}
          onRefresh={(isFull) => onRefresh(isFull || false)}
          isLoading={loading}   // ✅ ส่งต่อสถานะ Loading
        />
        {/* เพิ่ม Modal ไว้ล่างสุด */}
        <ImportAllConfirmModal
          isOpen={isAllModalOpen}
          count={displayUpdates.length}
          onClose={() => setIsAllModalOpen(false)}
          onConfirm={handleConfirmImportAll}
        />
        {/* <button
          onClick={onRefresh}
          disabled={loading}
          className="text-xs mb-2 text-white  py-1 rounded-md transition-all active:scale-95 disabled:opacity-50"
          style={{color: "var(--yellow-text-color)"}}
        >
          ดูทั้งหมด
        </button> */}
      </div>


      {/* Input ช่องค้นหา */}
      <div className="relative">
        <input
          type="text"
          placeholder="ค้นหาชื่อหุ้น (TICKER)..."
          value={searchTerm.toUpperCase()} // บังคับให้ในช่อง Input แสดงเป็นตัวพิมพ์ใหญ่
          onChange={(e) => setSearchTerm(e.target.value.toUpperCase())} // เก็บค่าเป็นตัวพิมพ์ใหญ่
          className="w-full bg-[var(--bg-item)] border border-[var(--border-item-dark)] rounded-lg mb-2 px-3 py-1.5 text-xs text-[var(--text-color)] placeholder:text-[var(--text-color)] placeholder:opacity-50 focus:outline-none focus:border-[var(--yellow-text-color)] transition-colors uppercase"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-color)] opacity-50 hover:opacity-100 text-xs"
          >
            ✕
          </button>
        )}
      </div>

      {error && (
        <div className="bg-[var(--theme-dark-red-bg)] border border-[var(--theme-dark-red-border)] text-[var(--red-text-color-btn)] p-3 rounded-lg text-sm mb-3">
          {error}
        </div>
      )}

      {/* Container หลักที่คุมการ Overflow */}
      <div className="overflow-hidden custom-scrollbar">
        {updates && updates.length > 0 ? (
          <div className="pb-2 gap-2 flex"
            style={{
              display: 'grid',
              gridTemplateRows: 'repeat(2, minmax(0, 1fr))', // ✅ กำหนดให้มี 2 แถวเท่ากัน
              gridAutoFlow: 'column', // ✅ บังคับให้ข้อมูลไหลไปทางขวา (สร้างคอลัมน์ใหม่ไปเรื่อยๆ)
              gap: '8px'
            }}>
            {displayUpdates.map((update, index) => (
              <div
                key={`${update.id}-${index}`}
                onClick={() => handleSelect(update.ticker)}
                /* เพิ่ม relative ที่นี่เพื่อให้ป้าย NEW อ้างอิงตำแหน่งได้ถูกต้อง */
                className="relative bg-[var(--realtime-component-item-bg)] border border-[var(--border-item-dark)] p-3 rounded-lg hover:bg-[var(--hover-item)] transition-colors cursor-pointer group shrink-0 w-[90px]"

              >

                {/* --- ส่วนที่เพิ่ม: ป้าย NEW --- */}
                {isNewSignal(update.created_at) && (
                  <div className="absolute -top-2 -right-2 z-9">
                    <span className="bg-[var(--new-badge-bg)] text-[var(--new-badge-text)] text-[6px] font-black px-1 py-0.5 rounded shadow-sm animate-pulse">
                      NEW
                    </span>
                  </div>
                )}

                <div className="flex flex-col justify-between items-start">
                  <div className="flex justify-between items-center gap-1">
                    <span className="font-bold text-sm tracking-tight" style={{ color: "var(--yellow-text-color)" }}>
                      {cryptoPairLabel(update.ticker)}
                    </span>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onImportClick(update.ticker);
                      }}
                      className="font-bold text-[7px] bg-[var(--button-bg-color)] hover:brightness-110 hover:scale-110 hover:shadow-[0_0_12px_rgba(37,99,235,0.8)] px-1 py-0.5 rounded transition-all"
                      style={{ color: 'var(--text-color)' }}
                    >
                      import
                    </button>
                  </div>
                  <span className="text-[10px] text-[var(--text-color)] opacity-60 font-medium">
                    {new Date(update.created_at).toLocaleTimeString('th-TH', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !loading && (
            <div className="flex flex-col items-center justify-center py-6 opacity-30 w-full text-[var(--text-color)]">
              <p className="text-xs">ไม่มีรายการอัปเดตใหม่</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SignalUpdateList;